import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { tokens } from "@/art/tokens";
import { AudioManager } from "@/engine/AudioManager";
import { cn } from "@/utils/cn";

type Variant = "primary" | "ghost" | "danger";
type Size = "s" | "m" | "l";

interface PrimaryButtonProps {
    children: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    variant?: Variant;
    size?: Size;
    className?: string;
    wide?: boolean;
    soundOnClick?: "tap" | "confirm" | "commit" | "none";
}

const sizeClass: Record<Size, string> = {
    s: "px-m py-xs text-small",
    m: "px-xl py-s text-body",
    l: "px-xxl py-m text-subtitle",
};

/**
 * 기본 게임 버튼. Framer Motion 탭 바운스 + 클릭 SFX 자동.
 * primary = ember, ghost = parchment-dim 외곽선, danger = burgundy.
 */
export function PrimaryButton({
    children,
    onClick,
    disabled,
    variant = "primary",
    size = "m",
    className,
    wide = false,
    soundOnClick = "tap",
}: PrimaryButtonProps) {
    const handleClick = () => {
        if (disabled) return;
        if (soundOnClick === "tap") AudioManager.sfx(tokens.audio.sfx.buttonTap);
        if (soundOnClick === "confirm") AudioManager.sfx(tokens.audio.sfx.buttonConfirm);
        if (soundOnClick === "commit") AudioManager.sfx(tokens.audio.sfx.choiceCommit);
        onClick?.();
    };

    const style = variantStyle(variant, disabled ?? false);

    return (
        <motion.button
            onClick={handleClick}
            disabled={disabled}
            whileHover={disabled ? undefined : { scale: 1.02, y: -1 }}
            whileTap={disabled ? undefined : { scale: 0.97 }}
            animate={variant === "primary" && !disabled
                ? {
                    boxShadow: [
                        `0 0 24px ${tokens.color.ember}33`,
                        `0 0 38px ${tokens.color.ember}57`,
                        `0 0 24px ${tokens.color.ember}33`,
                    ],
                }
                : undefined}
            transition={variant === "primary" && !disabled
                ? {
                    duration: 2.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                }
                : { duration: tokens.timing.buttonPress / 1000, ease: tokens.easing.standard }}
            className={cn(
                "relative overflow-hidden font-display rounded-m select-none transition-opacity whitespace-nowrap",
                sizeClass[size],
                wide && "w-full min-w-[280px]",
                disabled && "opacity-60 cursor-not-allowed",
                className,
            )}
            style={style}
        >
            {variant === "primary" && !disabled && (
                <motion.span
                    aria-hidden
                    className="absolute inset-y-0 -left-1/3 w-1/3"
                    animate={{ x: ["0%", "320%"] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", repeatDelay: 1.4 }}
                    style={{
                        background: `linear-gradient(90deg, ${tokens.color.parchment}00 0%, ${tokens.color.parchment}33 50%, ${tokens.color.parchment}00 100%)`,
                        transform: "skewX(-18deg)",
                        pointerEvents: "none",
                    }}
                />
            )}
            {children}
        </motion.button>
    );
}

function variantStyle(v: Variant, disabled: boolean): React.CSSProperties {
    if (disabled) {
        return {
            background: `${tokens.color.steel}20`,
            color: tokens.color.parchment,
            border: `1px solid ${tokens.color.steel}77`,
            letterSpacing: tokens.type.letterSpacing.wide,
            textShadow: `0 1px 0 ${tokens.color.ink}66`,
        };
    }
    if (v === "primary") {
        return {
            background: `linear-gradient(180deg, ${tokens.color.brass} 0%, ${tokens.color.ember} 52%, ${tokens.color.ember}CC 100%)`,
            color: tokens.color.parchment,
            border: `1px solid ${tokens.color.brass}`,
            boxShadow: `0 1px 0 ${tokens.color.parchment}24 inset, 0 0 18px ${tokens.color.ember}4D, 0 0 28px ${tokens.color.brass}29, 0 2px 6px ${tokens.color.ink}73`,
            letterSpacing: tokens.type.letterSpacing.wide,
            textShadow: `0 1px 0 ${tokens.color.ink}73, 0 0 10px ${tokens.color.parchment}2E`,
        };
    }
    if (v === "danger") {
        return {
            background: `linear-gradient(180deg, ${tokens.color.burgundy}28, ${tokens.color.burgundy}12)`,
            color: "#d39aaa",
            border: `1px solid ${tokens.color.burgundy}BB`,
            letterSpacing: tokens.type.letterSpacing.wide,
            boxShadow: `0 1px 0 ${tokens.color.parchment}14 inset, 0 0 16px ${tokens.color.burgundy}38`,
        };
    }
    // ghost — 양피지 얇은 선, 배경 힌트 있음
    return {
        background: `linear-gradient(180deg, ${tokens.color.parchment}10, ${tokens.color.parchment}06)`,
        color: tokens.color.parchment,
        border: `1px solid ${tokens.color.parchment}66`,
        letterSpacing: tokens.type.letterSpacing.wide,
        boxShadow: `0 1px 0 ${tokens.color.parchment}18 inset, 0 0 18px ${tokens.color.brass}22`,
        textShadow: `0 1px 0 ${tokens.color.ink}66`,
    };
}
